/**
 * EDITOR NATIVO DE PLANES — PLAN-UX-01-B
 *
 * Datos generales · Tareas · Programación. Editar sólo mientras el plan es
 * BORRADOR — el backend (0037, `fn_plan_lineas_solo_borrador`) ya lo
 * impone; esta pantalla oculta los controles de escritura para no ofrecer
 * una acción que el servidor va a rechazar, nunca como única barrera.
 *
 * ETIQUETA TEMPORAL, NO EL `estado` A SECAS
 *
 * "VIGENTE" en la base no siempre significa "aplicable hoy" desde
 * PLAN-UX-01-A (fecha de corte futura). `etiquetaTemporal()` usa la MISMA
 * regla de fechas que `planificacion.tsx` y que el motor — nunca inventa
 * una segunda semántica.
 */
import { useEffect, useId, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import {
  api,
  FalloApi,
  type AvisoPreviewPlan,
  type CambiosLinea,
  type Centro,
  type DatosDefaultPlan,
  type DatosLinea,
  type DecisionOtPublicacion,
  type DefaultAsignacionPlan,
  type EstrategiaPublicacion,
  type ImpactoHipotetico,
  type LineaPlan,
  type OperarioResumen,
  type Plan,
  type PlanDetalle,
  type PreviewProgramacionPlan,
  type ResultadoPublicarPlan,
  type TipoLimpieza,
  type Ubicacion,
  ETIQUETAS_TIPO_DOCUMENTO,
  type AmbitoAplicabilidadDocumento,
  type AplicabilidadDocumento,
  type DocumentoMaestro,
} from '../api.ts';
import {
  CampoTexto,
  Cargando,
  Cifra,
  Error as ErrorCaja,
  Modal,
  fecha,
  hoyLocal,
  useDatos,
  Vacio,
} from '../componentes/base.tsx';
import { POLITICAS_FESTIVO } from '../componentes/frecuencias.ts';
import {
  analizarRecurrencia,
  DIAS_SEMANA,
  ESTADO_INICIAL,
  POSICIONES_MENSUAL,
  resumenHumano,
  serializarRecurrencia,
  type DiaSemana,
  type EstadoRecurrencia,
  type FrecuenciaBase,
} from '../componentes/recurrencia.ts';
import { FormularioNuevaOt, GenerarOts } from './ots.tsx';
import { MaterialesTareaPlanSeccion } from './materiales-previstos.tsx';
import { clasificarFamilia } from './planificacion.tsx';
import { Shell } from '../componentes/shell.tsx';
import { useSesion } from '../contexto.tsx';

//  Fechas LOCALES: la vigencia de un plan es una fecha local (antes UTC: ver `hoyLocal`, UAT 9.6-B1-B).
function hoyIso(): string {
  return hoyLocal();
}

function manana(): string {
  return hoyLocal(1);
}

/** Mismo aspecto que `CampoTexto` (base.tsx), para los tipos que ésa no cubre: hora, fecha, número. */
function CampoInput({
  label,
  type,
  value,
  onChange,
  required,
  disabled,
}: {
  label: string;
  type: 'time' | 'date' | 'number';
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  disabled?: boolean;
}): ReactNode {
  const id = useId();
  return (
    <div className="campo">
      <label htmlFor={id}>{label}</label>
      <input id={id} type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} disabled={disabled} />
    </div>
  );
}

/** Misma regla que `planificacion.tsx:clasificarFamilia` — no se duplica la lógica, se repite la MISMA comparación. */
function etiquetaTemporal(plan: Plan): { texto: string; clase: string } {
  if (plan.estado === 'BORRADOR') return { texto: 'BORRADOR', clase: 'aviso' };
  const hoy = hoyIso();
  const rige = plan.vigenteDesde !== null && plan.vigenteDesde <= hoy && (plan.vigenteHasta === null || plan.vigenteHasta >= hoy);
  if (rige) return { texto: 'ACTUAL', clase: 'exito' };
  if (plan.estado === 'VIGENTE') return { texto: 'PRÓXIMA VERSIÓN', clase: 'info' };
  return { texto: 'HISTÓRICO', clase: 'neutra' };
}

/** Mensajes funcionales para los códigos de error que puede devolver el backend de planes — nunca el código crudo. */
function mensajeErrorPlan(e: unknown): string {
  if (!(e instanceof FalloApi)) return e instanceof Error ? e.message : 'Ha ocurrido un error.';
  switch (e.codigo) {
    case 'PLAN_SIN_LINEAS':
      return 'Este plan no tiene ninguna tarea. Añade al menos una antes de publicar.';
    case 'HORA_PROGRAMADA_REQUERIDA':
      return 'Indica la hora programada en «Datos generales» antes de publicar.';
    case 'FECHA_INVALIDA':
    case 'FECHA_CORTE_INVALIDA':
      return e.message;
    case 'PLAN_YA_PUBLICADO_CON_OTRA_VIGENCIA':
      return 'Este plan ya se publicó con otra fecha de entrada en vigor. Recarga la página para ver el estado actual.';
    case 'YA_EXISTE_BORRADOR':
      return 'Ya hay un borrador en curso para este plan. Continúa con ese en vez de crear otro.';
    case 'PLAN_NO_ES_BORRADOR':
      return 'Este plan ya no es un borrador: no se puede editar.';
    default:
      //  ASSIGN-DB-01 (0041): los rechazos de la base (operario no
      //  habilitado, etc.) llegan como REGLA_DE_NEGOCIO con el mensaje ya
      //  redactado para leerse (api/errores.ts) — se muestra tal cual,
      //  nunca el SQLSTATE ni el nombre del trigger.
      return e.message;
  }
}

// ------------------------------------------------------------------ página

/** F10.4 — los motivos de protección de una OT al republicar, como los diría quien planifica. */
const TEXTO_MOTIVO_PROTECCION: Readonly<Record<string, string>> = {
  ESTRUCTURA_CERRADA: 'ya terminó su ejecución',
  LINEA_CON_ACTIVIDAD: 'tiene tareas ya registradas',
  TIENE_EVIDENCIA: 'tiene fotos o evidencias',
  TIENE_NO_CONFORMIDAD: 'tiene una no conformidad',
  TIENE_CONSUMOS: 'tiene consumos de material',
  PREVISION_EDITADA: 'su material previsto se editó a mano',
  TIENE_ENTREGAS: 'tiene material entregado',
};

export function textoProteccion(motivos: readonly string[]): string {
  if (motivos.length === 0) return 'contiene actividad registrada';
  return motivos.map((m) => TEXTO_MOTIVO_PROTECCION[m] ?? m.toLowerCase().replaceAll('_', ' ')).join(', ');
}

export function PaginaEditorPlan(): ReactNode {
  const { idPlan = '' } = useParams();
  const { puede } = useSesion();
  const navegar = useNavigate();
  const [pestana, setPestana] = useState<'datos' | 'tareas' | 'asignaciones' | 'documentos' | 'programacion'>('datos');
  const [publicando, setPublicando] = useState(false);
  const [descartando, setDescartando] = useState(false);
  //  PLAN-UX-01-C, punto 10: acciones desde el Plan hacia el mundo de OT.
  const [nuevaOtDesdePlan, setNuevaOtDesdePlan] = useState(false);
  const [generarDesdePlan, setGenerarDesdePlan] = useState(false);

  const [creandoVersion, setCreandoVersion] = useState(false);
  const [errorVersion, setErrorVersion] = useState<unknown>(null);

  const plan = useDatos(() => api.get<PlanDetalle>(`/api/planes/${idPlan}`), [idPlan]);
  //  F10.4: desde «Planes» o desde la pestaña del centro, la miga vuelve a esa lista (con su búsqueda).
  const volver = (useLocation().state as { volver?: { a: string; texto: string } } | null)?.volver ?? null;
  //  Sólo hace falta el Centro completo para abrir esos dos formularios —
  //  se pide bajo demanda, no en cada carga de la página.
  const centroCompleto = useDatos(
    (): Promise<Centro | null> =>
      plan.datos === null || (!nuevaOtDesdePlan && !generarDesdePlan)
        ? Promise.resolve(null)
        : api.get<Centro>(`/api/centros/${plan.datos.idCentro}`),
    [plan.datos?.idCentro, nuevaOtDesdePlan, generarDesdePlan],
  );
  //  PLAN-UX-DEBT-03 (DEBT-ZERO-01): igual que `planificacion.tsx` —
  //  mismo `clasificarFamilia`, mismo endpoint — para que desde la FICHA
  //  del Plan (no sólo desde el listado del centro) se sepa si ya hay un
  //  borrador o una próxima versión antes de ofrecer "Crear nueva versión".
  const familiaPlanes = useDatos(
    () =>
      plan.datos === null
        ? Promise.resolve({ planes: [] as Plan[] })
        : api.get<{ planes: Plan[] }>(`/api/centros/${plan.datos.idCentro}/planes`),
    [plan.datos?.idCentro],
  );

  if (plan.cargando && plan.datos === null) {
    return (
      <Shell titulo="Plan">
        <Cargando />
      </Shell>
    );
  }
  if (plan.error !== null || plan.datos === null) {
    return (
      <Shell titulo="Plan">
        <ErrorCaja error={plan.error} reintentar={plan.recargar} />
      </Shell>
    );
  }

  const d = plan.datos;
  const esBorrador = d.estado === 'BORRADOR';
  const puedeEditar = puede('planes:editar') && esBorrador;
  const puedePublicar = puede('planes:publicar') && esBorrador;
  const et = etiquetaTemporal(d);
  const familia = clasificarFamilia(d.codigo, familiaPlanes.datos?.planes ?? []);
  //  Un sibling es "otro" si es una fila distinta de la que se está
  //  viendo ahora mismo — si YA estás en el borrador, no te ofrezcas a ti
  //  mismo como destino.
  const borradorAjeno = familia.borrador !== null && familia.borrador.id !== d.id ? familia.borrador : null;
  const proximaAjena = familia.proxima !== null && familia.proxima.id !== d.id ? familia.proxima : null;

  async function crearNuevaVersion(): Promise<void> {
    if (familia.actual === null) return;
    setCreandoVersion(true);
    setErrorVersion(null);
    try {
      const r = await api.post<{ idPlanBorrador: string }>(`/api/planes/${familia.actual.id}/nueva-version`);
      navegar(`/planes/${r.idPlanBorrador}`);
    } catch (e) {
      setErrorVersion(e);
    } finally {
      setCreandoVersion(false);
    }
  }

  return (
    <Shell
      migas={volver !== null ? [volver, { texto: `${d.codigo} v${String(d.version)}` }] : [
        { texto: 'Clientes', a: '/clientes' },
        { texto: d.clienteRazonSocial, a: `/clientes/${d.idCliente}` },
        { texto: d.centroCodigo, a: `/centros/${d.idCentro}/planificacion` },
        { texto: `${d.codigo} v${String(d.version)}` },
      ]}
      titulo={
        <span className="fila" style={{ gap: 10 }}>
          <span className="mono">{d.codigo}</span>
          <span>v{d.version}</span>
          <span className={`etiqueta ${et.clase}`}>{et.texto}</span>
        </span>
      }
      subtitulo={<span className="suave">{d.descripcion}</span>}
      acciones={
        <div className="fila" style={{ gap: 8 }}>
          {/*  PLAN-UX-01-C, punto 10: nunca sobre un BORRADOR — no hay nada
              estable que proponer ni ninguna vigencia real que generar. Se
              pasa la FAMILIA (d.codigo), nunca esta versión: si el usuario
              está viendo un histórico, la acción opera sobre la vigencia
              real de la familia, no sobre la versión antigua que mira. */}
          {!esBorrador && puede('ots:crear_manual') && (
            <button className="pequeno" onClick={() => setNuevaOtDesdePlan(true)}>
              Nueva OT basada en este plan
            </button>
          )}
          {!esBorrador && puede('ots:generar') && (
            <button className="pequeno" onClick={() => setGenerarDesdePlan(true)}>
              Generar próximas OTs
            </button>
          )}
          {puedeEditar && (
            <button className="pequeno" onClick={() => setDescartando(true)}>
              Descartar borrador
            </button>
          )}
          {puedePublicar && (
            <button className="principal" onClick={() => setPublicando(true)}>
              Publicar plan
            </button>
          )}
          {/*
            PLAN-UX-DEBT-04 (DEBT-ZERO-01): mismo listado global de OT que
            ya existe (`/ots`), filtrado por el CÓDIGO del plan con su
            propio buscador (`q`) — nunca un segundo gestor de OT dentro
            del Plan. El filtro queda visible y editable en el listado.
          */}
          {/*  F10.4: Plan → Planner con el centro y este plan ya elegidos; el Planner vuelve aquí. */}
          {!esBorrador && puede('ots:leer') && (
            <button className="pequeno" onClick={() => navegar(`/planner?idCliente=${d.idCliente}&idCentro=${d.idCentro}&idPlan=${d.id}&vista=semana`,
              { state: { volver: { a: `/planes/${d.id}`, texto: `${d.codigo} v${String(d.version)}` } } })}>
              Ver en el Planner
            </button>
          )}
          {d.otsGeneradas > 0 && puede('ots:leer') && (
            <button className="pequeno" onClick={() => navegar(`/ots?q=${encodeURIComponent(d.codigo)}`)}>
              Ver OTs generadas ({d.otsGeneradas})
            </button>
          )}
          {/*
            PLAN-UX-DEBT-03 (DEBT-ZERO-01): mismo criterio que ya usa
            `planificacion.tsx` (`BloqueFamilia`) — nunca un "Crear nueva
            versión" deshabilitado. Si ya hay un borrador o una próxima
            versión, se ofrece IR a esa, nunca la opción de crear otra.
          */}
          {borradorAjeno !== null ? (
            <button className="pequeno" onClick={() => navegar(`/planes/${borradorAjeno.id}`)}>
              Abrir próxima versión (v{borradorAjeno.version} · BORRADOR)
            </button>
          ) : proximaAjena !== null ? (
            <button className="pequeno" onClick={() => navegar(`/planes/${proximaAjena.id}`)}>
              Ver próxima versión (v{proximaAjena.version})
            </button>
          ) : (
            puede('planes:crear') &&
            familia.actual !== null &&
            familia.actual.id === d.id && (
              <button className="pequeno" disabled={creandoVersion} onClick={() => void crearNuevaVersion()}>
                {creandoVersion ? 'Creando…' : 'Crear nueva versión'}
              </button>
            )
          )}
        </div>
      }
      tabs={
        <nav className="tabs" aria-label="Secciones del plan">
          <button type="button" className={pestana === 'datos' ? 'activo' : ''} onClick={() => setPestana('datos')}>
            Datos generales
          </button>
          <button type="button" className={pestana === 'tareas' ? 'activo' : ''} onClick={() => setPestana('tareas')}>
            Tareas
          </button>
          <button type="button" className={pestana === 'asignaciones' ? 'activo' : ''} onClick={() => setPestana('asignaciones')}>
            Asignaciones
          </button>
          <button type="button" className={pestana === 'documentos' ? 'activo' : ''} onClick={() => setPestana('documentos')}>
            Documentos
          </button>
          <button type="button" className={pestana === 'programacion' ? 'activo' : ''} onClick={() => setPestana('programacion')}>
            Programación
          </button>
        </nav>
      }
    >
      {errorVersion !== null && (
        <ErrorCaja error={new Error(mensajeErrorPlan(errorVersion))} reintentar={() => void crearNuevaVersion()} />
      )}

      {!esBorrador && (
        <div className="nota aviso" role="status" style={{ marginBottom: 16 }}>
          Esta versión ya no es un borrador ({et.texto.toLowerCase()}): sólo lectura.
        </div>
      )}

      {nuevaOtDesdePlan && centroCompleto.datos !== null && (
        <div className="sep">
          <FormularioNuevaOt
            centroFijo={centroCompleto.datos}
            codigoPlanFijo={d.codigo}
            alCancelar={() => setNuevaOtDesdePlan(false)}
            alCrear={(ot) => navegar(`/ots/${ot.id}`)}
          />
        </div>
      )}
      {generarDesdePlan && centroCompleto.datos !== null && (
        <div className="sep">
          <GenerarOts
            idCentro={centroCompleto.datos.id}
            codigoPlanFijo={d.codigo}
            alCerrar={() => setGenerarDesdePlan(false)}
          />
        </div>
      )}

      {pestana === 'datos' && <TabDatos plan={d} puedeEditar={puedeEditar} recargar={plan.recargar} />}
      {pestana === 'tareas' && (
        <TabTareas
          idPlan={d.id}
          idCentro={d.idCentro}
          idCliente={d.idCliente}
          puedeEditar={puedeEditar}
          alCambiarLineas={plan.recargar}
        />
      )}
      {pestana === 'asignaciones' && (
        <TabAsignaciones idPlan={d.id} idCentro={d.idCentro} puedeEditar={puedeEditar} />
      )}
      {pestana === 'documentos' && <TabDocumentos idPlan={d.id} puedeEditar={puedeEditar} />}
      {pestana === 'programacion' && <TabProgramacion idPlan={d.id} />}

      {publicando && (
        <ModalPublicar
          plan={d}
          cerrar={() => setPublicando(false)}
          alPublicar={() => {
            setPublicando(false);
            plan.recargar();
          }}
        />
      )}
      {descartando && (
        <ModalDescartar
          idPlan={d.id}
          cerrar={() => setDescartando(false)}
          alDescartar={() => navegar(`/centros/${d.idCentro}/planificacion`)}
        />
      )}
    </Shell>
  );
}

// ----------------------------------------------------------- datos generales

function TabDatos({
  plan,
  puedeEditar,
  recargar,
}: {
  plan: PlanDetalle;
  puedeEditar: boolean;
  recargar: () => void;
}): ReactNode {
  const [descripcion, setDescripcion] = useState(plan.descripcion);
  const [horaProgramada, setHoraProgramada] = useState(plan.horaProgramada?.slice(0, 5) ?? '');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [guardado, setGuardado] = useState(false);

  const cambiado = descripcion.trim() !== plan.descripcion || horaProgramada !== (plan.horaProgramada?.slice(0, 5) ?? '');

  async function guardar(e: FormEvent): Promise<void> {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    setGuardado(false);
    try {
      await api.patch(`/api/planes/${plan.id}`, {
        descripcion: descripcion.trim(),
        horaProgramada: horaProgramada.trim() === '' ? null : horaProgramada,
      });
      setGuardado(true);
      recargar();
    } catch (err) {
      setError(err);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="panel">
      <div className="panel-cabecera">
        <h2>Datos generales</h2>
      </div>
      <div className="panel-cuerpo">
        <form className="form-inline" onSubmit={(e) => void guardar(e)}>
          <div className="form-grupo">
            <CampoTexto label="Código" value={plan.codigo} onChange={() => undefined} disabled />
            <CampoTexto
              label="Descripción"
              value={descripcion}
              onChange={setDescripcion}
              disabled={!puedeEditar || enviando}
              required
            />
            <CampoInput
              label="Hora programada"
              type="time"
              value={horaProgramada}
              onChange={setHoraProgramada}
              disabled={!puedeEditar || enviando}
            />
            <div className="campo">
              <label>Entrada en vigor</label>
              <div className="valor-dato">
                {plan.vigenteDesde === null
                  ? 'Se determinará al publicar'
                  : `${fecha(plan.vigenteDesde)} → ${plan.vigenteHasta === null ? 'sin fin' : fecha(plan.vigenteHasta)}`}
              </div>
            </div>
          </div>
          {error !== null && <ErrorCaja error={error} />}
          {guardado && !cambiado && <p className="pequena suave">Guardado.</p>}
          {puedeEditar && (
            <div className="fila-botones">
              <button type="submit" className="principal" disabled={enviando || !cambiado}>
                {enviando ? 'Guardando…' : 'Guardar cambios'}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------- tareas

function claveGrupo(l: LineaPlan): string {
  if (l.ubicacionRuta === null) return l.ubicacion;
  return l.ubicacionRuta.split(' > ')[0] ?? l.ubicacion;
}

function TabTareas({
  idPlan,
  idCentro,
  idCliente,
  puedeEditar,
  alCambiarLineas,
}: {
  idPlan: string;
  idCentro: string;
  idCliente: string;
  puedeEditar: boolean;
  /** El recuento de tareas del plan (cabecera, resumen de publicar) vive en `/api/planes/:id`, no aquí: hay que refrescarlo también. */
  alCambiarLineas: () => void;
}): ReactNode {
  const lineas = useDatos(() => api.get<{ lineas: LineaPlan[] }>(`/api/planes/${idPlan}/lineas`), [idPlan]);
  const ubicaciones = useDatos(() => api.get<{ ubicaciones: Ubicacion[] }>(`/api/centros/${idCentro}/ubicaciones`), [idCentro]);
  const tipos = useDatos(() => api.get<{ tiposLimpieza: TipoLimpieza[] }>(`/api/clientes/${idCliente}/tipos-limpieza`), [idCliente]);

  const [busqueda, setBusqueda] = useState('');
  const [soloFuera, setSoloFuera] = useState(false);
  const [seleccion, setSeleccion] = useState<ReadonlySet<string>>(new Set());
  const [colapsados, setColapsados] = useState<ReadonlySet<string>>(new Set());
  const [editando, setEditando] = useState<{ linea: LineaPlan | null; duplicarDe: LineaPlan | null } | null>(null);
  const [borrando, setBorrando] = useState<LineaPlan | null>(null);
  //  FASE 9.6-B1: en una versión publicada las tareas no se editan, pero su material previsto se consulta.
  const [verMateriales, setVerMateriales] = useState<LineaPlan | null>(null);
  const [errorAccion, setErrorAccion] = useState<unknown>(null);

  const filtradas = useMemo(() => {
    const t = busqueda.trim().toLowerCase();
    return (lineas.datos?.lineas ?? []).filter((l) => {
      if (soloFuera && !l.fueraContrato) return false;
      if (t === '') return true;
      return [l.referenciaTarea, l.ubicacion, l.descripcionTarea ?? '', l.tipo, l.frecuencia].join(' ').toLowerCase().includes(t);
    });
  }, [lineas.datos, busqueda, soloFuera]);

  const grupos = useMemo(() => {
    const m = new Map<string, LineaPlan[]>();
    for (const l of filtradas) {
      const clave = claveGrupo(l);
      const lista = m.get(clave) ?? [];
      lista.push(l);
      m.set(clave, lista);
    }
    return [...m.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [filtradas]);

  function alternarGrupo(clave: string): void {
    setColapsados((prev) => {
      const s = new Set(prev);
      if (s.has(clave)) s.delete(clave);
      else s.add(clave);
      return s;
    });
  }

  function alternarSeleccion(id: string): void {
    setSeleccion((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return s;
    });
  }

  async function eliminarSeleccionadas(): Promise<void> {
    setErrorAccion(null);
    try {
      await Promise.all([...seleccion].map((id) => api.del(`/api/planes/${idPlan}/lineas/${id}`)));
      setSeleccion(new Set());
      lineas.recargar();
      alCambiarLineas();
    } catch (e) {
      setErrorAccion(e);
    }
  }

  async function cambiarPoliticaSeleccionadas(politicaFestivo: string): Promise<void> {
    setErrorAccion(null);
    try {
      await Promise.all(
        [...seleccion].map((id) => api.patch(`/api/planes/${idPlan}/lineas/${id}`, { politicaFestivo })),
      );
      lineas.recargar();
    } catch (e) {
      setErrorAccion(e);
    }
  }

  if (lineas.cargando && lineas.datos === null) return <Cargando />;
  if (lineas.error !== null) return <ErrorCaja error={lineas.error} reintentar={lineas.recargar} />;

  return (
    <div className="panel">
      <div className="panel-cabecera">
        <h2>Tareas</h2>
        <div className="fila" style={{ gap: 8 }}>
          <span className="suave pequena">
            {filtradas.length} de {lineas.datos?.lineas.length ?? 0}
          </span>
          {puedeEditar && (
            <button className="principal pequeno" onClick={() => setEditando({ linea: null, duplicarDe: null })}>
              Añadir tarea
            </button>
          )}
        </div>
      </div>

      <div className="filtros">
        <div className="campo campo-busqueda">
          <label htmlFor="buscar-tarea-editor">Buscar</label>
          <input
            id="buscar-tarea-editor"
            type="text"
            placeholder="Referencia, ubicación, descripción…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
        </div>
        <div className="campo">
          <label htmlFor="fuera-editor">Clasificación</label>
          <select id="fuera-editor" value={soloFuera ? 'fuera' : 'todas'} onChange={(e) => setSoloFuera(e.target.value === 'fuera')}>
            <option value="todas">Todas</option>
            <option value="fuera">Sólo fuera de contrato</option>
          </select>
        </div>
      </div>

      {puedeEditar && seleccion.size > 0 && (
        <div className="barra-bulk">
          <div className="fila barra-bulk-fila">
            <button className="pequeno" onClick={() => setSeleccion(new Set())}>
              Limpiar selección
            </button>
            <span className="pequena suave barra-bulk-contador">{seleccion.size} seleccionada{seleccion.size === 1 ? '' : 's'}</span>
            <span className="barra-bulk-relleno" />
            <select
              className="pequeno"
              defaultValue=""
              onChange={(e) => {
                if (e.target.value !== '') void cambiarPoliticaSeleccionadas(e.target.value);
                e.target.value = '';
              }}
            >
              <option value="" disabled>
                Cambiar política de festivo…
              </option>
              {POLITICAS_FESTIVO.map((p) => (
                <option key={p.valor} value={p.valor}>
                  {p.etiqueta}
                </option>
              ))}
            </select>
            <button className="peligro pequeno" onClick={() => void eliminarSeleccionadas()}>
              Eliminar seleccionadas
            </button>
          </div>
        </div>
      )}
      {errorAccion !== null && (
        <div className="panel-cuerpo">
          <ErrorCaja error={errorAccion} />
        </div>
      )}

      <div className="panel-cuerpo sin-relleno">
        {grupos.length === 0 ? (
          <Vacio titulo={lineas.datos?.lineas.length === 0 ? 'Este plan todavía no tiene tareas' : 'Ninguna tarea coincide'} />
        ) : (
          <>
            <div className="tabla-desliza tabla-lineas-desktop">
              <table>
                <thead>
                  <tr>
                    {puedeEditar && <th className="col-check" aria-label="Seleccionar" />}
                    <th>Referencia</th>
                    <th>Descripción</th>
                    <th>Tipo</th>
                    <th>Frecuencia</th>
                    <th>Festivos</th>
                    <th>Contrato</th>
                    <th />
                  </tr>
                </thead>
                {grupos.map(([grupo, filas]) => (
                  <tbody key={grupo}>
                    <tr className="fila-grupo-ubicacion">
                      <td colSpan={puedeEditar ? 8 : 7}>
                        <button type="button" className="grupo-toggle" onClick={() => alternarGrupo(grupo)}>
                          <span aria-hidden="true">{colapsados.has(grupo) ? '▶' : '▼'}</span> {grupo}
                          <span className="suave pequena"> ({filas.length})</span>
                        </button>
                      </td>
                    </tr>
                    {!colapsados.has(grupo) &&
                      filas.map((l) => (
                        <tr key={l.id}>
                          {puedeEditar && (
                            <td className="col-check">
                              <input
                                type="checkbox"
                                aria-label={`Seleccionar ${l.referenciaTarea}`}
                                checked={seleccion.has(l.id)}
                                onChange={() => alternarSeleccion(l.id)}
                              />
                            </td>
                          )}
                          <td className="cod">{l.referenciaTarea}</td>
                          <td>{l.descripcionTarea ?? '—'}</td>
                          <td className="cod">{l.tipo}</td>
                          <td>{l.frecuencia}</td>
                          <td className="suave pequena">{l.politicaFestivo}</td>
                          <td>
                            {l.fueraContrato ? <span className="etiqueta aviso">FUERA</span> : <span className="suave pequena">Incluida</span>}
                          </td>
                          {puedeEditar && (
                            <td className="derecha">
                              <div className="fila" style={{ gap: 6, justifyContent: 'flex-end' }}>
                                <button className="pequeno" onClick={() => setEditando({ linea: l, duplicarDe: null })}>
                                  Editar
                                </button>
                                <button className="pequeno" onClick={() => setEditando({ linea: null, duplicarDe: l })}>
                                  Duplicar
                                </button>
                                <button className="pequeno peligro" onClick={() => setBorrando(l)}>
                                  Eliminar
                                </button>
                              </div>
                            </td>
                          )}
                          {!puedeEditar && (
                            <td className="derecha">
                              <button className="pequeno" onClick={() => setVerMateriales(l)}>Materiales</button>
                            </td>
                          )}
                        </tr>
                      ))}
                  </tbody>
                ))}
              </table>
            </div>

            <ul className="lista-lineas-movil">
              {grupos.map(([grupo, filas]) => (
                <li key={grupo}>
                  <button type="button" className="grupo-toggle" onClick={() => alternarGrupo(grupo)} style={{ width: '100%', textAlign: 'left' }}>
                    <span aria-hidden="true">{colapsados.has(grupo) ? '▶' : '▼'}</span> {grupo}
                    <span className="suave pequena"> ({filas.length})</span>
                  </button>
                  {!colapsados.has(grupo) && (
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                      {filas.map((l) => (
                        <li key={l.id} className="tarjeta-linea">
                          <div className="tarjeta-linea-cabecera">
                            <div>
                              <div className="cod">{l.referenciaTarea}</div>
                              <div className="pequena suave">{l.descripcionTarea ?? '—'}</div>
                            </div>
                            {l.fueraContrato && <span className="etiqueta aviso">FUERA</span>}
                          </div>
                          <div className="tarjeta-linea-datos">
                            <span>{l.tipo}</span>
                            <span>{l.frecuencia}</span>
                            <span className="tenue">{l.politicaFestivo}</span>
                          </div>
                          {puedeEditar && (
                            <div className="tarjeta-linea-acciones fila" style={{ gap: 8, flexWrap: 'wrap' }}>
                              <button className="pequeno" onClick={() => setEditando({ linea: l, duplicarDe: null })}>
                                Editar
                              </button>
                              <button className="pequeno" onClick={() => setEditando({ linea: null, duplicarDe: l })}>
                                Duplicar
                              </button>
                              <button className="pequeno peligro" onClick={() => setBorrando(l)}>
                                Eliminar
                              </button>
                            </div>
                          )}
                          {!puedeEditar && (
                            <div className="tarjeta-linea-acciones fila" style={{ gap: 8, flexWrap: 'wrap' }}>
                              <button className="pequeno" onClick={() => setVerMateriales(l)}>Materiales</button>
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      {verMateriales !== null && (
        <Modal titulo={`Materiales de «${verMateriales.referenciaTarea}»`} cerrar={() => setVerMateriales(null)}>
          <MaterialesTareaPlanSeccion idPlan={idPlan} idLinea={verMateriales.id} />
        </Modal>
      )}
      {editando !== null && (
        <ModalTarea
          idPlan={idPlan}
          linea={editando.linea}
          duplicarDe={editando.duplicarDe}
          ubicaciones={ubicaciones.datos?.ubicaciones ?? []}
          tipos={tipos.datos?.tiposLimpieza ?? []}
          cerrar={() => setEditando(null)}
          alGuardar={() => {
            setEditando(null);
            lineas.recargar();
            alCambiarLineas();
          }}
        />
      )}
      {borrando !== null && (
        <Modal titulo="Eliminar tarea" cerrar={() => setBorrando(null)}>
          <p>
            ¿Eliminar «<strong>{borrando.referenciaTarea}</strong>» ({borrando.descripcionTarea ?? '—'})? Esta acción no se puede deshacer.
          </p>
          <div className="fila-botones">
            <button
              className="peligro"
              onClick={() => {
                void api.del(`/api/planes/${idPlan}/lineas/${borrando.id}`).then(() => {
                  setBorrando(null);
                  lineas.recargar();
                  alCambiarLineas();
                });
              }}
            >
              Eliminar
            </button>
            <button onClick={() => setBorrando(null)}>Cancelar</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ------------------------------------------------------- modal añadir/editar

function ModalTarea({
  idPlan,
  linea,
  duplicarDe,
  ubicaciones,
  tipos,
  cerrar,
  alGuardar,
}: {
  idPlan: string;
  /** No-nulo = editar esta línea. */
  linea: LineaPlan | null;
  /** No-nulo = precargar sus datos, pero crear una línea NUEVA con referencia propia. */
  duplicarDe: LineaPlan | null;
  ubicaciones: readonly Ubicacion[];
  tipos: readonly TipoLimpieza[];
  cerrar: () => void;
  alGuardar: () => void;
}): ReactNode {
  const base = linea ?? duplicarDe;
  const esEdicion = linea !== null;

  const [idUbicacion, setIdUbicacion] = useState(base?.idUbicacion ?? '');
  const [idTipo, setIdTipo] = useState(base?.idTipo ?? '');
  const [descripcionTarea, setDescripcionTarea] = useState(base?.descripcionTarea ?? '');
  const [recurrencia, setRecurrencia] = useState<EstadoRecurrencia>(() =>
    base ? analizarRecurrencia(base.frecuencia, base.rrule) : ESTADO_INICIAL,
  );
  const [fechaInicio, setFechaInicio] = useState(base?.fechaInicio ?? '');
  const [politicaFestivo, setPoliticaFestivo] = useState(base?.politicaFestivo ?? 'SALTAR');
  const [fueraContrato, setFueraContrato] = useState(base?.fueraContrato ?? false);
  const [duracionEstMin, setDuracionEstMin] = useState(base?.duracionEstMin?.toString() ?? '');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const esSegunNecesidad = recurrencia.freq === 'S.N.';

  function actualizarRecurrencia(cambios: Partial<EstadoRecurrencia>): void {
    setRecurrencia((r) => ({ ...r, ...cambios }));
  }

  function alternarDia(dia: DiaSemana): void {
    setRecurrencia((r) => ({
      ...r,
      byday: r.byday.includes(dia) ? r.byday.filter((d) => d !== dia) : [...r.byday, dia],
    }));
  }

  async function guardar(e: FormEvent): Promise<void> {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      const { frecuenciaEtiqueta, rrule } = serializarRecurrencia(recurrencia);
      const datos: DatosLinea = {
        idUbicacion,
        idTipo,
        descripcionTarea: descripcionTarea.trim(),
        frecuenciaEtiqueta,
        rrule,
        fechaInicioRecurrencia: esSegunNecesidad ? null : fechaInicio || null,
        duracionEstMin: duracionEstMin.trim() === '' ? null : Number(duracionEstMin),
        politicaFestivo,
        fueraContrato,
      };
      if (esEdicion) {
        const cambios: CambiosLinea = {
          idUbicacion: datos.idUbicacion,
          idTipo: datos.idTipo,
          descripcionTarea: datos.descripcionTarea,
          frecuenciaEtiqueta: datos.frecuenciaEtiqueta,
          rrule: datos.rrule,
          fechaInicioRecurrencia: datos.fechaInicioRecurrencia,
          duracionEstMin: datos.duracionEstMin,
          politicaFestivo: datos.politicaFestivo,
          fueraContrato: datos.fueraContrato,
        };
        await api.patch(`/api/planes/${idPlan}/lineas/${linea!.id}`, cambios);
      } else {
        await api.post(`/api/planes/${idPlan}/lineas`, datos);
      }
      alGuardar();
    } catch (err) {
      setError(err);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal
      titulo={esEdicion ? `Editar «${linea!.referenciaTarea}»` : duplicarDe !== null ? `Duplicar «${duplicarDe.referenciaTarea}»` : 'Añadir tarea'}
      cerrar={cerrar}
      pie={
        <>
          <span className="modal-pie-relleno" />
          {error !== null && <ErrorCaja error={error} />}
          <button type="submit" form="form-tarea" className="principal" disabled={enviando}>
            {enviando ? 'Guardando…' : esEdicion ? 'Guardar cambios' : 'Añadir tarea'}
          </button>
        </>
      }
    >
      {duplicarDe !== null && (
        <p className="pequena suave">
          La copia recibirá su propia referencia — nunca reutiliza «{duplicarDe.referenciaTarea}»: son dos tareas
          lógicas distintas.
        </p>
      )}
      {!esEdicion && esEdicion === false && duplicarDe === null && (
        <p className="pequena tenue">La referencia de esta tarea la genera NeatOS automáticamente.</p>
      )}
      <form id="form-tarea" onSubmit={(e) => void guardar(e)}>
        <div className="form-grupo">
          <div className="campo">
            <label htmlFor="tarea-ubicacion">Ubicación</label>
            <select id="tarea-ubicacion" value={idUbicacion} onChange={(e) => setIdUbicacion(e.target.value)} required disabled={enviando}>
              <option value="" disabled>
                Elige una ubicación…
              </option>
              {ubicaciones.map((u) => (
                <option key={u.id} value={u.id}>
                  {'  '.repeat(u.nivel)}{u.codigo} — {u.descripcion}
                </option>
              ))}
            </select>
          </div>
          <div className="campo">
            <label htmlFor="tarea-tipo">Tipo de limpieza</label>
            <select id="tarea-tipo" value={idTipo} onChange={(e) => setIdTipo(e.target.value)} required disabled={enviando}>
              <option value="" disabled>
                Elige un tipo…
              </option>
              {tipos.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.codigo} — {t.descripcion}
                </option>
              ))}
            </select>
          </div>
          <CampoTexto label="Descripción de la tarea" value={descripcionTarea} onChange={setDescripcionTarea} required disabled={enviando} />

          <div className="campo">
            <label htmlFor="tarea-frecuencia">Frecuencia</label>
            <select
              id="tarea-frecuencia"
              value={recurrencia.freq}
              onChange={(e) => actualizarRecurrencia({ freq: e.target.value as FrecuenciaBase })}
              disabled={enviando}
            >
              <option value="DIARIA">Diaria</option>
              <option value="SEMANAL">Semanal</option>
              <option value="MENSUAL">Mensual</option>
              <option value="ANUAL">Anual</option>
              <option value="S.N.">Según necesidad (sin recurrencia)</option>
            </select>
          </div>

          {!esSegunNecesidad && (
            <>
              <CampoInput
                label="Fecha de inicio de la recurrencia"
                type="date"
                value={fechaInicio}
                onChange={setFechaInicio}
                required
                disabled={enviando}
              />
              <CampoInput
                label={recurrencia.freq === 'SEMANAL' ? 'Cada cuántas semanas' : recurrencia.freq === 'MENSUAL' ? 'Cada cuántos meses' : recurrencia.freq === 'ANUAL' ? 'Cada cuántos años' : 'Cada cuántos días'}
                type="number"
                value={String(recurrencia.intervalo)}
                onChange={(v) => actualizarRecurrencia({ intervalo: Math.max(1, Number(v) || 1) })}
                disabled={enviando}
              />
            </>
          )}

          {!esSegunNecesidad && recurrencia.freq === 'SEMANAL' && (
            <div className="campo">
              <label>Días de la semana</label>
              <div className="fila" style={{ gap: 4, flexWrap: 'wrap' }}>
                {DIAS_SEMANA.map((d) => (
                  <button
                    key={d.codigo}
                    type="button"
                    className={recurrencia.byday.includes(d.codigo) ? 'principal' : ''}
                    style={{ width: 34, height: 34, padding: 0 }}
                    onClick={() => alternarDia(d.codigo)}
                    disabled={enviando}
                    title={d.larga}
                    aria-pressed={recurrencia.byday.includes(d.codigo)}
                  >
                    {d.corta}
                  </button>
                ))}
              </div>
              <p className="pequena tenue">
                Sin ningún día marcado, se repite el mismo día de la semana que la fecha de inicio.
              </p>
            </div>
          )}

          {!esSegunNecesidad && recurrencia.freq === 'MENSUAL' && (
            <div className="campo">
              <label>Modo</label>
              <div className="fila" style={{ gap: 12 }}>
                <label className="fila" style={{ gap: 6 }}>
                  <input
                    type="radio"
                    name="modo-mensual"
                    checked={recurrencia.modoMensual === 'DIA'}
                    onChange={() => actualizarRecurrencia({ modoMensual: 'DIA' })}
                    disabled={enviando}
                  />
                  Día del mes (según la fecha de inicio)
                </label>
                <label className="fila" style={{ gap: 6 }}>
                  <input
                    type="radio"
                    name="modo-mensual"
                    checked={recurrencia.modoMensual === 'POSICION'}
                    onChange={() => actualizarRecurrencia({ modoMensual: 'POSICION' })}
                    disabled={enviando}
                  />
                  Posición (p. ej. «el primer lunes»)
                </label>
              </div>
              {recurrencia.modoMensual === 'POSICION' && (
                <div className="fila" style={{ gap: 8, marginTop: 8 }}>
                  <select
                    value={recurrencia.posicionMensual}
                    onChange={(e) => actualizarRecurrencia({ posicionMensual: Number(e.target.value) })}
                    disabled={enviando}
                  >
                    {POSICIONES_MENSUAL.map((p) => (
                      <option key={p.valor} value={p.valor}>
                        {p.etiqueta}
                      </option>
                    ))}
                  </select>
                  <select
                    value={recurrencia.diaSemanaMensual}
                    onChange={(e) => actualizarRecurrencia({ diaSemanaMensual: e.target.value as DiaSemana })}
                    disabled={enviando}
                  >
                    {DIAS_SEMANA.map((d) => (
                      <option key={d.codigo} value={d.codigo}>
                        {d.larga}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {!esSegunNecesidad && (
            <p className="pequena suave" style={{ gridColumn: '1 / -1' }}>
              <strong>Resumen:</strong> {resumenHumano(recurrencia)}
            </p>
          )}

          <div className="campo">
            <label htmlFor="tarea-festivo">Política de festivo</label>
            <select id="tarea-festivo" value={politicaFestivo} onChange={(e) => setPoliticaFestivo(e.target.value)} disabled={enviando}>
              {POLITICAS_FESTIVO.map((p) => (
                <option key={p.valor} value={p.valor}>
                  {p.etiqueta}
                </option>
              ))}
            </select>
          </div>
          <CampoInput
            label="Duración estimada (minutos)"
            type="number"
            value={duracionEstMin}
            onChange={setDuracionEstMin}
            disabled={enviando}
          />
          <label className="fila" style={{ gap: 6 }}>
            <input type="checkbox" checked={fueraContrato} onChange={(e) => setFueraContrato(e.target.checked)} disabled={enviando} />
            Fuera de contrato
          </label>
        </div>
      </form>
      {/* FASE 9.6-B1: material habitual de la tarea (fuera del <form>: tiene sus propias acciones). */}
      {esEdicion ? (
        <MaterialesTareaPlanSeccion idPlan={idPlan} idLinea={linea.id} />
      ) : (
        <p className="suave pequena">Guarda la tarea para añadirle los materiales previstos.</p>
      )}
    </Modal>
  );
}

// --------------------------------------------------------- asignaciones (ASSIGN-UX-01B)

/**
 * Para cada línea del plan, qué operarios la cubren HOY — derivado en el
 * cliente a partir de los defaults, nunca recalculado por el backend: es
 * exactamente la misma noción de cobertura que ya resuelve `v_ot_operario_lineas`
 * para una OT real (TODA_OT cubre todas las líneas, LINEAS sólo las suyas),
 * trasladada a la plantilla.
 */
function coberturaDePlan(
  defaults: readonly DefaultAsignacionPlan[],
  lineas: readonly LineaPlan[],
): ReadonlyMap<string, string[]> {
  const porLinea = new Map<string, string[]>(lineas.map((l) => [l.id, []]));
  for (const d of defaults) {
    const idsLinea = d.alcance === 'TODA_OT' ? lineas.map((l) => l.id) : d.idsPlanLineas;
    for (const idLinea of idsLinea) porLinea.get(idLinea)?.push(d.operarioCodigo);
  }
  return porLinea;
}

function comoDatosDefault(d: DefaultAsignacionPlan): DatosDefaultPlan {
  return { idOperario: d.idOperario, alcance: d.alcance, idsPlanLineas: d.idsPlanLineas };
}

/**
 * Igual que `coberturaDePlan`, pero sin necesitar la lista de líneas: para
 * el resumen del modal de publicar basta con el RECUENTO, y `plan.lineas`
 * (ya cargado en esa pantalla) es el total. Un TODA_OT cubre todo por
 * definición; si no hay ninguno, la cobertura es la unión de las líneas de
 * los defaults LINEAS.
 */
function resumenCobertura(
  defaults: readonly DefaultAsignacionPlan[],
  totalLineas: number,
): { cubiertas: number; sinAsignar: number } {
  if (defaults.some((d) => d.alcance === 'TODA_OT')) {
    return { cubiertas: totalLineas, sinAsignar: 0 };
  }
  const cubiertas = new Set(defaults.flatMap((d) => d.idsPlanLineas)).size;
  return { cubiertas, sinAsignar: Math.max(0, totalLineas - cubiertas) };
}

// ------------------------------------------------------- documentos (4.4-F)

/**
 * Asociación de documentos del catálogo a este Plan — Plan completo o
 * líneas concretas. Reemplaza TODO el conjunto al guardar (mismo
 * criterio que Asignaciones/`establecerDefaultsPlan`): la pantalla manda
 * siempre la lista completa. Sólo editable mientras `puedeEditar`
 * (BORRADOR) — el trigger de 0043 es la barrera real.
 */
function TabDocumentos({ idPlan, puedeEditar }: { idPlan: string; puedeEditar: boolean }): ReactNode {
  const estado = useDatos(() => api.get<{ filas: AplicabilidadDocumento[] }>(`/api/planes/${idPlan}/documentos`), [idPlan]);
  const lineasEstado = useDatos(() => api.get<{ lineas: LineaPlan[] }>(`/api/planes/${idPlan}/lineas`), [idPlan]);
  const catalogoEstado = useDatos(() => api.get<{ filas: DocumentoMaestro[] }>('/api/documentos?estado=ACTIVO'), []);

  const [pendientes, setPendientes] = useState<AplicabilidadDocumento[] | null>(null);
  const [anadiendo, setAnadiendo] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  const lista = pendientes ?? estado.datos?.filas ?? null;
  const sinGuardar = pendientes !== null;

  function quitar(id: string): void {
    setPendientes((lista ?? []).filter((f) => f.id !== id));
    setMensaje(null);
  }

  function anadir(f: { idDocumentoMaestro: string; codigo: string; titulo: string; tipoDocumento: DocumentoMaestro['tipoDocumento']; ambito: AmbitoAplicabilidadDocumento; idPlanLinea: string | null }): void {
    setPendientes([...(lista ?? []), { id: `pendiente-${f.idDocumentoMaestro}-${f.idPlanLinea ?? 'plan'}`, ...f }]);
    setAnadiendo(false);
    setMensaje(null);
  }

  async function guardar(): Promise<void> {
    if (pendientes === null) return;
    setGuardando(true);
    setError(null);
    try {
      //  El backend agrupa por documento+ambito: cada fila PLAN_LINEA se
      //  manda tal cual, una por línea — el servicio las agrupa igual que
      //  ya hacía `establecerDefaultsPlan` con LINEAS.
      await api.put(`/api/planes/${idPlan}/documentos`, {
        documentos: pendientes.map((f) => ({
          idDocumentoMaestro: f.idDocumentoMaestro,
          ambito: f.ambito,
          idsPlanLineas: f.idPlanLinea === null ? undefined : [f.idPlanLinea],
        })),
      });
      setPendientes(null);
      setMensaje('Documentos guardados.');
      estado.recargar();
    } catch (e) {
      setError(e);
    } finally {
      setGuardando(false);
    }
  }

  if (estado.cargando && estado.datos === null) return <Cargando texto="Cargando documentos del plan…" />;
  if (estado.error !== null) return <ErrorCaja error={estado.error} reintentar={estado.recargar} />;

  return (
    <div className="panel">
      <div className="panel-cabecera">
        <h2>Documentos aplicables</h2>
        {puedeEditar && !anadiendo && (
          <button type="button" className="pequeno" onClick={() => setAnadiendo(true)}>Añadir documento</button>
        )}
      </div>
      <div className="panel-cuerpo">
        {mensaje !== null && <div className="nota exito" role="status">{mensaje}</div>}
        {error !== null && <ErrorCaja error={error} />}
        {anadiendo && catalogoEstado.datos !== null && lineasEstado.datos !== null && (
          <FormularioAnadirDocumento
            catalogo={catalogoEstado.datos.filas}
            lineas={lineasEstado.datos.lineas}
            cancelar={() => setAnadiendo(false)}
            alAnadir={anadir}
          />
        )}
        {lista === null || lista.length === 0 ? (
          <Vacio titulo="Este plan no tiene documentos asociados" />
        ) : (
          <ul className="lista-nc-lineas">
            {lista.map((f) => (
              <li key={f.id}>
                <span className="etiqueta info">{ETIQUETAS_TIPO_DOCUMENTO[f.tipoDocumento] ?? f.tipoDocumento}</span>
                <span className="mono pequena">{f.codigo}</span>
                <span className="pequena">{f.titulo}</span>
                <span className="pequena tenue">
                  {f.ambito === 'PLAN' ? 'Toda la OT' : `Línea ${lineasEstado.datos?.lineas.find((l) => l.id === f.idPlanLinea)?.ubicacion ?? f.idPlanLinea ?? ''}`}
                </span>
                {puedeEditar && (
                  <button type="button" className="pequeno" onClick={() => quitar(f.id)}>Quitar</button>
                )}
              </li>
            ))}
          </ul>
        )}
        {puedeEditar && sinGuardar && (
          <div className="fila" style={{ gap: 8, marginTop: 12 }}>
            <button type="button" className="principal" disabled={guardando} onClick={() => void guardar()}>
              {guardando ? 'Guardando…' : 'Guardar cambios'}
            </button>
            <button type="button" className="pequeno" disabled={guardando} onClick={() => setPendientes(null)}>
              Descartar cambios
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function FormularioAnadirDocumento({
  catalogo, lineas, cancelar, alAnadir,
}: {
  catalogo: readonly DocumentoMaestro[];
  lineas: readonly LineaPlan[];
  cancelar: () => void;
  alAnadir: (f: { idDocumentoMaestro: string; codigo: string; titulo: string; tipoDocumento: DocumentoMaestro['tipoDocumento']; ambito: AmbitoAplicabilidadDocumento; idPlanLinea: string | null }) => void;
}): ReactNode {
  const idBase = useId();
  const [idDocumentoMaestro, setIdDocumentoMaestro] = useState(catalogo[0]?.id ?? '');
  const [ambito, setAmbito] = useState<AmbitoAplicabilidadDocumento>('PLAN');
  const [idLinea, setIdLinea] = useState(lineas[0]?.id ?? '');
  const [error, setError] = useState<string | null>(null);

  function confirmar(): void {
    const doc = catalogo.find((d) => d.id === idDocumentoMaestro);
    if (doc === undefined) {
      setError('Elige un documento del catálogo.');
      return;
    }
    if (ambito === 'PLAN_LINEA' && idLinea === '') {
      setError('Elige una línea.');
      return;
    }
    alAnadir({
      idDocumentoMaestro, codigo: doc.codigo, titulo: doc.titulo, tipoDocumento: doc.tipoDocumento,
      ambito, idPlanLinea: ambito === 'PLAN_LINEA' ? idLinea : null,
    });
  }

  if (catalogo.length === 0) {
    return (
      <div className="panel accion-linea">
        <div className="panel-cuerpo">
          <p className="pequena">No hay documentos activos en el catálogo. Crea uno primero en «Documentos».</p>
          <button type="button" className="pequeno" onClick={cancelar}>Cerrar</button>
        </div>
      </div>
    );
  }

  return (
    <div className="panel accion-linea">
      <div className="panel-cabecera">
        <h3>Añadir documento</h3>
        <button type="button" className="pequeno" onClick={cancelar}>Cancelar</button>
      </div>
      <div className="panel-cuerpo">
        {error !== null && <p className="nota error" role="alert">{error}</p>}
        <div className="campo">
          <label htmlFor={`${idBase}-doc`}>Documento</label>
          <select id={`${idBase}-doc`} value={idDocumentoMaestro} onChange={(e) => setIdDocumentoMaestro(e.target.value)}>
            {catalogo.map((d) => (
              <option key={d.id} value={d.id}>{d.codigo} — {d.titulo}</option>
            ))}
          </select>
        </div>
        <div className="campo">
          <span className="etiqueta-dato">Ámbito</span>
          <div className="fila" style={{ gap: 16 }}>
            <label className="fila" style={{ gap: 6 }}>
              <input type="radio" checked={ambito === 'PLAN'} onChange={() => setAmbito('PLAN')} />
              Toda la OT
            </label>
            <label className="fila" style={{ gap: 6 }}>
              <input type="radio" checked={ambito === 'PLAN_LINEA'} onChange={() => setAmbito('PLAN_LINEA')} />
              Una línea concreta
            </label>
          </div>
        </div>
        {ambito === 'PLAN_LINEA' && (
          <div className="campo">
            <label htmlFor={`${idBase}-linea`}>Línea</label>
            <select id={`${idBase}-linea`} value={idLinea} onChange={(e) => setIdLinea(e.target.value)}>
              {lineas.map((l) => (
                <option key={l.id} value={l.id}>{l.ubicacion} — {l.descripcionTarea ?? l.tipo}</option>
              ))}
            </select>
          </div>
        )}
        <button type="button" className="principal" onClick={confirmar}>Añadir</button>
      </div>
    </div>
  );
}

function TabAsignaciones({
  idPlan,
  idCentro,
  puedeEditar,
}: {
  idPlan: string;
  idCentro: string;
  puedeEditar: boolean;
}): ReactNode {
  const defaults = useDatos(
    () => api.get<{ defaults: DefaultAsignacionPlan[] }>(`/api/planes/${idPlan}/operarios`),
    [idPlan],
  );
  const lineas = useDatos(() => api.get<{ lineas: LineaPlan[] }>(`/api/planes/${idPlan}/lineas`), [idPlan]);
  //  Sólo operarios habilitados en ESTE centro (§5) — el filtro lo aplica
  //  `/api/operarios?idCentro=`, no esta pantalla.
  const operariosCentro = useDatos(
    () => api.get<{ operarios: OperarioResumen[] }>(`/api/operarios?idCentro=${idCentro}&activo=true&dominio=LIMPIEZA`),
    [idCentro],
  );

  const [vista, setVista] = useState<'operario' | 'tarea'>('operario');
  const [asignando, setAsignando] = useState<{
    preseleccion: readonly string[];
    modoInicial: 'TODA_OT' | 'LINEAS';
    lineasIniciales: readonly string[];
  } | null>(null);
  const [retirando, setRetirando] = useState<DefaultAsignacionPlan | null>(null);
  const [enviandoRetiro, setEnviandoRetiro] = useState(false);
  const [errorAccion, setErrorAccion] = useState<unknown>(null);

  if ((defaults.cargando && defaults.datos === null) || (lineas.cargando && lineas.datos === null)) {
    return <Cargando />;
  }
  if (defaults.error !== null) return <ErrorCaja error={defaults.error} reintentar={defaults.recargar} />;
  if (lineas.error !== null) return <ErrorCaja error={lineas.error} reintentar={lineas.recargar} />;

  const listaDefaults = defaults.datos?.defaults ?? [];
  const listaLineas = lineas.datos?.lineas ?? [];
  const porLinea = coberturaDePlan(listaDefaults, listaLineas);
  const sinAsignar = listaLineas.filter((l) => (porLinea.get(l.id) ?? []).length === 0);

  async function guardar(nuevos: DatosDefaultPlan[]): Promise<void> {
    await api.put(`/api/planes/${idPlan}/operarios`, { defaults: nuevos });
    defaults.recargar();
  }

  async function retirar(d: DefaultAsignacionPlan): Promise<void> {
    setEnviandoRetiro(true);
    setErrorAccion(null);
    try {
      await guardar(listaDefaults.filter((x) => x.idOperario !== d.idOperario).map(comoDatosDefault));
      setRetirando(null);
    } catch (e) {
      setErrorAccion(e);
    } finally {
      setEnviandoRetiro(false);
    }
  }

  async function cambiarATodaOt(d: DefaultAsignacionPlan): Promise<void> {
    setErrorAccion(null);
    try {
      await guardar(
        listaDefaults.map((x) =>
          x.idOperario === d.idOperario ? { idOperario: x.idOperario, alcance: 'TODA_OT' as const, idsPlanLineas: [] } : comoDatosDefault(x),
        ),
      );
    } catch (e) {
      setErrorAccion(e);
    }
  }

  return (
    <div className="panel">
      <div className="panel-cabecera">
        <h2>Asignaciones por defecto</h2>
        {puedeEditar && (
          <button
            type="button"
            className="principal pequeno"
            onClick={() => setAsignando({ preseleccion: [], modoInicial: 'TODA_OT', lineasIniciales: [] })}
          >
            Asignar operarios
          </button>
        )}
      </div>
      <div className="panel-cuerpo">
        <p className="pequena suave">
          Estas asignaciones se aplicarán automáticamente a las próximas órdenes de trabajo que genere esta versión
          del plan. Las OT ya emitidas nunca cambian.
        </p>

        <div className="cifras">
          <Cifra n={listaDefaults.length} t={listaDefaults.length === 1 ? 'operario con asignación' : 'operarios con asignación'} />
          <Cifra n={listaLineas.length - sinAsignar.length} t={`de ${listaLineas.length} tareas cubiertas`} />
          <Cifra n={sinAsignar.length} t="tareas sin asignar" />
        </div>

        {sinAsignar.length > 0 && (
          <div className="nota aviso" role="status">
            {sinAsignar.length} tarea{sinAsignar.length === 1 ? '' : 's'} sin asignación por defecto. El plan puede
            publicarse igualmente: nadie queda bloqueado por esto.
          </div>
        )}

        {errorAccion !== null && <ErrorCaja error={errorAccion} />}

        <div className="fila sep" style={{ gap: 8 }}>
          <button type="button" className={vista === 'operario' ? 'pequeno activo' : 'pequeno'} onClick={() => setVista('operario')}>
            Por operario
          </button>
          <button type="button" className={vista === 'tarea' ? 'pequeno activo' : 'pequeno'} onClick={() => setVista('tarea')}>
            Por tarea
          </button>
        </div>

        {vista === 'operario' ? (
          listaDefaults.length === 0 ? (
            <Vacio titulo="Todavía no hay operarios con asignación por defecto en este plan">
              {puedeEditar && (
                <p className="pequena suave">
                  Usa «Asignar operarios» para que las nuevas OT de este plan nazcan ya con cobertura.
                </p>
              )}
            </Vacio>
          ) : (
            <ListaPorOperario
              defaults={listaDefaults}
              totalLineas={listaLineas.length}
              puedeEditar={puedeEditar}
              onCambiarLineas={(d) =>
                setAsignando({ preseleccion: [d.idOperario], modoInicial: 'LINEAS', lineasIniciales: d.idsPlanLineas })
              }
              onCambiarATodaOt={(d) => void cambiarATodaOt(d)}
              onRetirar={setRetirando}
            />
          )
        ) : (
          <ListaPorTarea lineas={listaLineas} porLinea={porLinea} />
        )}
      </div>

      {asignando !== null && (
        <ModalAsignarOperarios
          defaults={listaDefaults}
          lineas={listaLineas}
          porLinea={porLinea}
          operarios={operariosCentro.datos?.operarios ?? null}
          preseleccion={asignando.preseleccion}
          modoInicial={asignando.modoInicial}
          lineasIniciales={asignando.lineasIniciales}
          guardar={guardar}
          cerrar={() => setAsignando(null)}
        />
      )}

      {retirando !== null && (
        <Modal
          titulo="Retirar de las asignaciones por defecto"
          cerrar={() => setRetirando(null)}
          pie={
            <>
              <span className="modal-pie-relleno" />
              <button className="peligro" disabled={enviandoRetiro} onClick={() => void retirar(retirando)}>
                {enviandoRetiro ? 'Retirando…' : 'Retirar'}
              </button>
              <button disabled={enviandoRetiro} onClick={() => setRetirando(null)}>
                Cancelar
              </button>
            </>
          }
        >
          <p>
            «{retirando.operarioNombre}» dejará de tener asignación por defecto en esta versión del plan. Las OT que
            ya se generaron con este operario no cambian.
          </p>
        </Modal>
      )}
    </div>
  );
}

function ListaPorOperario({
  defaults,
  totalLineas,
  puedeEditar,
  onCambiarLineas,
  onCambiarATodaOt,
  onRetirar,
}: {
  defaults: readonly DefaultAsignacionPlan[];
  totalLineas: number;
  puedeEditar: boolean;
  onCambiarLineas: (d: DefaultAsignacionPlan) => void;
  onCambiarATodaOt: (d: DefaultAsignacionPlan) => void;
  onRetirar: (d: DefaultAsignacionPlan) => void;
}): ReactNode {
  function coberturaTexto(d: DefaultAsignacionPlan): string {
    if (d.alcance === 'TODA_OT') return `Todas las tareas (${String(totalLineas)})`;
    const n = d.idsPlanLineas.length;
    return `${String(n)} tarea${n === 1 ? '' : 's'}`;
  }

  return (
    <>
      <div className="tabla-desliza tabla-lineas-desktop">
        <table>
          <thead>
            <tr>
              <th>Operario</th>
              <th>Alcance</th>
              <th>Cobertura</th>
              {puedeEditar && <th />}
            </tr>
          </thead>
          <tbody>
            {defaults.map((d) => (
              <tr key={d.idPlanOperario}>
                <td>
                  {d.operarioNombre} <span className="pequena suave">({d.operarioCodigo})</span>
                </td>
                <td>{d.alcance === 'TODA_OT' ? 'Toda la OT' : 'Líneas concretas'}</td>
                <td className="pequena">{coberturaTexto(d)}</td>
                {puedeEditar && (
                  <td className="derecha">
                    <div className="fila" style={{ gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                      <button type="button" className="pequeno" onClick={() => onCambiarLineas(d)}>
                        Cambiar líneas
                      </button>
                      {d.alcance === 'LINEAS' && (
                        <button type="button" className="pequeno" onClick={() => onCambiarATodaOt(d)}>
                          Pasar a toda la OT
                        </button>
                      )}
                      <button type="button" className="pequeno peligro" onClick={() => onRetirar(d)}>
                        Retirar
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ul className="lista-lineas-movil">
        {defaults.map((d) => (
          <li key={d.idPlanOperario} className="tarjeta-linea">
            <div className="tarjeta-linea-cabecera">
              <div>
                <div className="cod">{d.operarioCodigo}</div>
                <div className="pequena suave">{d.operarioNombre}</div>
              </div>
              <span className={`etiqueta ${d.alcance === 'TODA_OT' ? 'exito' : 'info'}`}>
                {d.alcance === 'TODA_OT' ? 'Toda la OT' : 'Líneas'}
              </span>
            </div>
            <div className="tarjeta-linea-datos">
              <span>{coberturaTexto(d)}</span>
            </div>
            {puedeEditar && (
              <div className="tarjeta-linea-acciones fila" style={{ gap: 8, flexWrap: 'wrap' }}>
                <button type="button" className="pequeno" onClick={() => onCambiarLineas(d)}>
                  Cambiar líneas
                </button>
                {d.alcance === 'LINEAS' && (
                  <button type="button" className="pequeno" onClick={() => onCambiarATodaOt(d)}>
                    Pasar a toda la OT
                  </button>
                )}
                <button type="button" className="pequeno peligro" onClick={() => onRetirar(d)}>
                  Retirar
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </>
  );
}

/** §9: la misma cobertura, mirada desde la tarea en vez de desde el operario — para detectar huecos rápido. */
function ListaPorTarea({
  lineas,
  porLinea,
}: {
  lineas: readonly LineaPlan[];
  porLinea: ReadonlyMap<string, string[]>;
}): ReactNode {
  if (lineas.length === 0) return <Vacio titulo="Este plan todavía no tiene tareas" />;

  function celda(codigos: readonly string[]): ReactNode {
    return codigos.length === 0 ? (
      <span className="etiqueta aviso pequena">Sin asignar</span>
    ) : (
      <span className="pequena">{codigos.join(', ')}</span>
    );
  }

  return (
    <>
      <div className="tabla-desliza tabla-lineas-desktop">
        <table>
          <thead>
            <tr>
              <th>Tarea</th>
              <th>Ubicación</th>
              <th>Operarios</th>
            </tr>
          </thead>
          <tbody>
            {lineas.map((l) => (
              <tr key={l.id}>
                <td>{l.descripcionTarea ?? l.tipo}</td>
                <td className="cod">{l.ubicacion}</td>
                <td>{celda(porLinea.get(l.id) ?? [])}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ul className="lista-lineas-movil">
        {lineas.map((l) => (
          <li key={l.id} className="tarjeta-linea">
            <div className="tarjeta-linea-cabecera">
              <div>
                <div className="cod">{l.ubicacion}</div>
                <div className="pequena suave">{l.descripcionTarea ?? l.tipo}</div>
              </div>
            </div>
            <div className="tarjeta-linea-datos">{celda(porLinea.get(l.id) ?? [])}</div>
          </li>
        ))}
      </ul>
    </>
  );
}

/**
 * §4/§7 — un único modo de trabajo: "estado deseado para los operarios
 * seleccionados", nunca ANADIR/SUSTITUIR/RETIRAR como en la OT real
 * (0010/ASSIGN-01). Aquí no hace falta: la plantilla de un Plan permite
 * como mucho un default por operario (0041, `ux_plan_operarios_operario`),
 * así que "asignar" es siempre "fijar" el alcance y las líneas de quien
 * se seleccione, sin tocar a los demás operarios. El conflicto TODA_OT ↔
 * LINEAS del mismo operario (regla que en la base es un rechazo, nunca un
 * estado válido) se anticipa aquí mostrando qué va a cambiar ANTES de
 * enviar nada — nunca deja que llegue un error crudo del servidor.
 */
function ModalAsignarOperarios({
  defaults,
  lineas,
  porLinea,
  operarios,
  preseleccion,
  modoInicial,
  lineasIniciales,
  guardar,
  cerrar,
}: {
  defaults: readonly DefaultAsignacionPlan[];
  lineas: readonly LineaPlan[];
  porLinea: ReadonlyMap<string, string[]>;
  operarios: readonly OperarioResumen[] | null;
  preseleccion: readonly string[];
  modoInicial: 'TODA_OT' | 'LINEAS';
  lineasIniciales: readonly string[];
  guardar: (nuevos: DatosDefaultPlan[]) => Promise<void>;
  cerrar: () => void;
}): ReactNode {
  const [alcance, setAlcance] = useState<'TODA_OT' | 'LINEAS'>(modoInicial);
  const [idsOperarios, setIdsOperarios] = useState<ReadonlySet<string>>(new Set(preseleccion));
  const [idsLineas, setIdsLineas] = useState<ReadonlySet<string>>(new Set(lineasIniciales));
  const [busquedaLineas, setBusquedaLineas] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const defaultsPorOperario = useMemo(() => new Map(defaults.map((d) => [d.idOperario, d])), [defaults]);

  function alternarOperario(id: string): void {
    setIdsOperarios((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return s;
    });
  }
  function alternarLinea(id: string): void {
    setIdsLineas((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return s;
    });
  }

  const lineasFiltradas = useMemo(() => {
    const t = busquedaLineas.trim().toLowerCase();
    if (t === '') return lineas;
    return lineas.filter((l) => `${l.ubicacion} ${l.tipo} ${l.descripcionTarea ?? ''}`.toLowerCase().includes(t));
  }, [lineas, busquedaLineas]);

  //  Operarios seleccionados que YA tenían un default con OTRO alcance:
  //  esto es exactamente lo que el encargo pide anticipar (§7), antes de
  //  que exista ninguna petición de red.
  const cambiosDeAlcance = [...idsOperarios]
    .map((id) => defaultsPorOperario.get(id))
    .filter((d): d is DefaultAsignacionPlan => d !== undefined && d.alcance !== alcance);

  async function aplicar(): Promise<void> {
    if (idsOperarios.size === 0) {
      setError(new Error('Selecciona al menos un operario.'));
      return;
    }
    if (alcance === 'LINEAS' && idsLineas.size === 0) {
      setError(new Error('Selecciona al menos una línea para este alcance.'));
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      const sinTocar = defaults.filter((d) => !idsOperarios.has(d.idOperario)).map(comoDatosDefault);
      const actualizados: DatosDefaultPlan[] = [...idsOperarios].map((idOperario) => ({
        idOperario,
        alcance,
        idsPlanLineas: alcance === 'LINEAS' ? [...idsLineas] : [],
      }));
      await guardar([...sinTocar, ...actualizados]);
      cerrar();
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal
      titulo="Asignar operarios"
      cerrar={cerrar}
      pie={
        <>
          <span className="modal-pie-relleno" />
          {error !== null && <ErrorCaja error={new Error(mensajeErrorPlan(error))} />}
          <button type="button" className="principal" disabled={enviando} onClick={() => void aplicar()}>
            {enviando ? 'Aplicando…' : 'Aplicar'}
          </button>
        </>
      }
    >
      <div className="fila" style={{ gap: 16, flexWrap: 'wrap' }}>
        <label className="fila" style={{ gap: 6 }}>
          <input type="radio" checked={alcance === 'TODA_OT'} onChange={() => setAlcance('TODA_OT')} disabled={enviando} />
          Toda la OT
        </label>
        <label className="fila" style={{ gap: 6 }}>
          <input type="radio" checked={alcance === 'LINEAS'} onChange={() => setAlcance('LINEAS')} disabled={enviando} />
          Líneas seleccionadas
        </label>
      </div>
      <p className="pequena suave">
        {alcance === 'TODA_OT'
          ? 'Los operarios elegidos quedarán cubriendo todas las tareas de las OT que genere este plan, incluidas las que se añadan más adelante.'
          : 'Los operarios elegidos sólo cubrirán las tareas que selecciones abajo.'}
      </p>

      <div className="sep">
        <h3>Operarios</h3>
        {operarios === null ? (
          <Cargando texto="Cargando operarios habilitados en el centro…" />
        ) : operarios.length === 0 ? (
          <p className="pequena suave">Ningún operario está habilitado en el centro de este plan.</p>
        ) : (
          <ul className="lista-checks lista-checks-horizontal">
            {operarios.map((op) => {
              const previo = defaultsPorOperario.get(op.id);
              const cambia = idsOperarios.has(op.id) && previo !== undefined && previo.alcance !== alcance;
              return (
                <li key={op.id}>
                  <label className="fila" style={{ gap: 6 }}>
                    <input type="checkbox" checked={idsOperarios.has(op.id)} onChange={() => alternarOperario(op.id)} disabled={enviando} />
                    <span>
                      {op.nombre} {op.apellidos} ({op.codigo})
                    </span>
                    {!op.activo && <span className="etiqueta neutra pequena">inactivo</span>}
                  </label>
                  {cambia && (
                    <div className="pequena suave" style={{ marginLeft: 24 }}>
                      Actualmente: {previo.alcance === 'TODA_OT' ? 'Toda la OT' : `${String(previo.idsPlanLineas.length)} línea(s)`} → pasará a{' '}
                      {alcance === 'TODA_OT' ? 'Toda la OT' : 'líneas concretas'}.
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {cambiosDeAlcance.length > 0 && (
          <div className="nota aviso" role="status">
            Un operario no puede estar a la vez en «Toda la OT» y en «Líneas»: al aplicar, su asignación anterior se
            sustituye por la nueva — nunca se suman las dos.
          </div>
        )}
      </div>

      {alcance === 'LINEAS' && (
        <div className="sep">
          <h3>Líneas</h3>
          <div className="fila" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
            <div className="campo campo-busqueda" style={{ marginBottom: 0 }}>
              <label htmlFor="asignar-buscar-linea">Filtrar</label>
              <input
                id="asignar-buscar-linea"
                type="text"
                placeholder="Ubicación o tarea…"
                value={busquedaLineas}
                onChange={(e) => setBusquedaLineas(e.target.value)}
              />
            </div>
            <button type="button" className="pequeno" onClick={() => setIdsLineas(new Set(lineasFiltradas.map((l) => l.id)))} disabled={enviando}>
              Seleccionar todas
            </button>
            <button type="button" className="pequeno" onClick={() => setIdsLineas(new Set())} disabled={enviando || idsLineas.size === 0}>
              Limpiar selección
            </button>
            <span className="pequena suave">
              {idsLineas.size} de {lineas.length} seleccionadas
            </span>
          </div>
          <ul className="lista-checks" style={{ maxHeight: 260, overflowY: 'auto' }}>
            {lineasFiltradas.map((l) => (
              <li key={l.id}>
                <label className="fila" style={{ gap: 8 }}>
                  <input type="checkbox" checked={idsLineas.has(l.id)} onChange={() => alternarLinea(l.id)} disabled={enviando} />
                  <span className="pequena">
                    {l.ubicacion} — {l.descripcionTarea ?? l.tipo}
                  </span>
                  <span className="pequena suave" style={{ marginLeft: 'auto' }}>
                    {(porLinea.get(l.id) ?? []).join(', ') || 'sin asignar'}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Modal>
  );
}

// ------------------------------------------------------------- programación

function TabProgramacion({ idPlan }: { idPlan: string }): ReactNode {
  const preview = useDatos(() => api.get<PreviewProgramacionPlan>(`/api/planes/${idPlan}/programacion-preview`), [idPlan]);

  if (preview.cargando && preview.datos === null) return <Cargando texto="Calculando la programación…" />;
  if (preview.error !== null) return <ErrorCaja error={preview.error} reintentar={preview.recargar} />;
  const d = preview.datos;
  if (d === null) return null;

  return (
    <div className="panel">
      <div className="panel-cabecera">
        <h2>Programación</h2>
        <span className="suave pequena">
          {fecha(d.desde)} → {fecha(d.hasta)}
        </span>
      </div>
      <div className="panel-cuerpo">
        <p className="suave pequena">Si publicas este plan ahora, esto es lo que generaría NeatOS. No se ha creado ninguna orden de trabajo.</p>
        <p className="suave pequena">
          Las nuevas OT heredarán las asignaciones por defecto configuradas en la pestaña «Asignaciones» de esta
          versión.
        </p>
        <div className="cifras">
          <Cifra n={d.totalLineas} t="tareas en total" />
          <Cifra n={d.lineasSegunNecesidad} t="según necesidad" />
          <Cifra n={d.lineasFueraContrato} t="fuera de contrato" />
          <Cifra n={d.totalOcurrenciasEnHorizonte} t="ocurrencias en 3 semanas" />
        </div>

        {d.avisos.length > 0 && (
          <div className="sep">
            <p className="pequena suave">Posibles problemas de configuración:</p>
            <ul className="lista-rechazadas">
              {d.avisos.map((a, i) => (
                <AvisoItem key={`${a.idLinea}-${String(i)}`} aviso={a} />
              ))}
            </ul>
          </div>
        )}

        <div className="sep">
          <h3>Próximas ocurrencias</h3>
          {d.proximasOcurrencias.length === 0 ? (
            <p className="suave pequena">Ninguna tarea con recurrencia produce ocurrencias en las próximas 3 semanas.</p>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {d.proximasOcurrencias.map((o, i) => (
                <li key={`${o.idLinea}-${o.fecha}-${String(i)}`} className="fila" style={{ gap: 10, padding: '6px 0', borderBottom: '1px solid var(--borde)' }}>
                  <span className="mono pequena">{o.fechaHora !== null ? fechaHoraCorta(o.fechaHora) : fecha(o.fecha)}</span>
                  <span className="cod pequena">{o.ubicacionCodigo}</span>
                  <span className="pequena">{o.descripcionTarea}</span>
                  {o.desplazada && <span className="etiqueta info pequena">desplazada</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function fechaHoraCorta(iso: string): string {
  const [f, h] = iso.split('T');
  return `${fecha(f ?? null)} ${h?.slice(0, 5) ?? ''}`;
}

function AvisoItem({ aviso }: { aviso: AvisoPreviewPlan }): ReactNode {
  return (
    <li>
      <div className="pequena suave">{aviso.mensaje}</div>
    </li>
  );
}

// --------------------------------------------------------------- publicar

//  FASE 6.5, FASE O: texto en lenguaje humano de cada estrategia. Las
//  letras quedan como referencia secundaria, nunca como texto principal.
const TEXTO_ESTRATEGIA: Record<EstrategiaPublicacion, { titulo: string; explicacion: string }> = {
  A: {
    titulo: 'Mantener las OTs ya emitidas',
    explicacion: 'Las OTs existentes no cambian. El nuevo plan se aplicará a futuras emisiones.',
  },
  B: {
    titulo: 'Aplicar únicamente cambios compatibles',
    explicacion:
      'Sólo confirma, sin tocar nada, las OTs cuyo cambio es puramente de etiqueta y no afecta a lo ya emitido.',
  },
  C: {
    titulo: 'Anular y volver a emitir',
    explicacion: 'Las OTs elegibles se anularán y se crearán nuevas desde el plan actualizado.',
  },
  D: {
    titulo: 'Revisar una por una',
    explicacion: 'Permite decidir individualmente qué hacer con cada OT afectada.',
  },
};

const TEXTO_DECISION_OT: Record<DecisionOtPublicacion, string> = {
  MANTENER: 'Mantener',
  APLICAR_COMPATIBLE: 'Aplicar compatible',
  ANULAR_REEMITIR: 'Anular y reemitir',
};

function ModalPublicar({
  plan,
  cerrar,
  alPublicar,
}: {
  plan: PlanDetalle;
  cerrar: () => void;
  alPublicar: () => void;
}): ReactNode {
  const preview = useDatos(() => api.get<PreviewProgramacionPlan>(`/api/planes/${plan.id}/programacion-preview`), [plan.id]);
  //  §11: resumen de asignaciones por defecto en el mismo modal de
  //  publicar. Sólo lectura: publicar no cambia ni valida los defaults.
  const defaultsAsignacion = useDatos(
    () => api.get<{ defaults: DefaultAsignacionPlan[] }>(`/api/planes/${plan.id}/operarios`),
    [plan.id],
  );
  const [fechaCorte, setFechaCorte] = useState(manana());
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [resultado, setResultado] = useState<ResultadoPublicarPlan | null>(null);
  const [estrategia, setEstrategia] = useState<EstrategiaPublicacion>('A');
  const [decisiones, setDecisiones] = useState<Record<string, DecisionOtPublicacion>>({});
  const [motivo, setMotivo] = useState('');
  //  PLAN-UX-01-C, punto 11 + FASE 6.5: informe de impacto ANTES de
  //  publicar, con la MISMA fecha de corte que se está a punto de elegir —
  //  se recalcula si esa fecha cambia. `evaluarImpactoVersion` nunca
  //  escribe nada (READ ONLY); la clasificación por estrategia tampoco.
  const impacto = useDatos(
    () => api.get<ImpactoHipotetico>(`/api/planes/${plan.id}/impacto-hipotetico?fechaCorte=${fechaCorte}`),
    [plan.id, fechaCorte],
  );
  const datosImpacto = impacto.datos !== null && !impacto.datos.sinVersionAnterior ? impacto.datos : null;
  const hayDecisionQueTomar = datosImpacto !== null && datosImpacto.otsImpactadas > 0;

  //  Si cambia la fecha de corte (y por tanto el informe), una estrategia
  //  ya no disponible o unas decisiones de OTs que ya no existen se
  //  descartan — nunca se manda al backend una elección obsoleta.
  useEffect(() => {
    if (datosImpacto === null) {
      setEstrategia('A');
      setDecisiones({});
      return;
    }
    if (!datosImpacto.estrategiasDisponibles.includes(estrategia)) setEstrategia('A');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datosImpacto]);

  function decisionDe(ot: { idOt: string; clasificacion: string; protegida: boolean }): DecisionOtPublicacion {
    if (ot.protegida) return 'MANTENER';
    return decisiones[ot.idOt] ?? 'MANTENER';
  }

  async function publicar(): Promise<void> {
    setEnviando(true);
    setError(null);
    try {
      const cuerpo: {
        fechaCorte: string;
        estrategia?: EstrategiaPublicacion;
        decisiones?: { idOt: string; decision: DecisionOtPublicacion }[];
        motivo?: string;
      } = { fechaCorte };
      if (hayDecisionQueTomar) {
        cuerpo.estrategia = estrategia;
        if (estrategia === 'D' && datosImpacto !== null) {
          cuerpo.decisiones = datosImpacto.ots.map((o) => ({ idOt: o.idOt, decision: decisionDe(o) }));
        }
        if ((estrategia === 'C' || estrategia === 'D') && motivo.trim() !== '') cuerpo.motivo = motivo.trim();
      }
      const r = await api.post<ResultadoPublicarPlan>(`/api/planes/${plan.id}/publicar`, cuerpo);
      setResultado(r);
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  if (resultado !== null) {
    return (
      <Modal titulo="Plan publicado" cerrar={alPublicar} pie={<button className="principal" onClick={alPublicar}>Cerrar</button>}>
        <p>
          «{resultado.codigo}» v{resultado.version} es ahora la versión vigente, con entrada en vigor el {fecha(resultado.vigenteDesde)}.
        </p>
        {resultado.sustituyoA !== null && <p className="suave pequena">La versión anterior queda como histórico a partir de esa fecha.</p>}
        {resultado.resumenOts.length > 0 && (
          <div className="sep">
            <h3>Qué se hizo con las OTs afectadas</h3>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {resultado.resumenOts.map((o) => (
                <li key={o.idOt} className="fila" style={{ gap: 10, padding: '6px 0', borderBottom: '1px solid var(--borde)' }}>
                  <span className="cod pequena">{o.codigoOt}</span>
                  <span className="pequena">{TEXTO_DECISION_OT[o.decision]!}</span>
                  {o.resultado === 'RECHAZADA' && <span className="etiqueta aviso pequena">no aplicada</span>}
                  {o.codigoOtNueva !== null && (
                    <span className="pequena suave">→ nueva OT {o.codigoOtNueva}</span>
                  )}
                  {o.detalle !== null && <span className="pequena suave">{o.detalle}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </Modal>
    );
  }

  return (
    <Modal
      titulo="Publicar plan"
      cerrar={cerrar}
      pie={
        <>
          <span className="modal-pie-relleno" />
          {error !== null && <ErrorCaja error={new Error(mensajeErrorPlan(error))} />}
          <button className="principal" disabled={enviando} onClick={() => void publicar()}>
            {enviando ? 'Publicando…' : 'Publicar plan'}
          </button>
        </>
      }
    >
      <div className="datos">
        <div className="dato">
          <div className="etiqueta-dato">Versión</div>
          <div className="valor-dato">v{plan.version}</div>
        </div>
        <div className="dato">
          <div className="etiqueta-dato">Tareas</div>
          <div className="valor-dato">{plan.lineas}</div>
        </div>
        <div className="dato">
          <div className="etiqueta-dato">Hora programada</div>
          <div className="valor-dato">{plan.horaProgramada?.slice(0, 5) ?? '— (indícala en Datos generales)'}</div>
        </div>
      </div>
      <div className="campo" style={{ marginTop: 12 }}>
        <label htmlFor="publicar-fecha-corte">Fecha de entrada en vigor</label>
        <input
          id="publicar-fecha-corte"
          type="date"
          value={fechaCorte}
          min={hoyIso()}
          onChange={(e) => setFechaCorte(e.target.value)}
        />
      </div>
      {preview.datos !== null && preview.datos.avisos.length > 0 && (
        <div className="sep">
          <p className="pequena suave">Avisos detectados en la programación:</p>
          <ul className="lista-rechazadas">
            {preview.datos.avisos.map((a, i) => (
              <li key={`${a.idLinea}-${String(i)}`}>
                <div className="pequena suave">{a.mensaje}</div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="sep">
        <h3>Informe de impacto</h3>
        {impacto.cargando && impacto.datos === null && <p className="pequena suave">Calculando impacto…</p>}
        {impacto.datos !== null && impacto.datos.sinVersionAnterior && (
          <p className="pequena suave">Es la primera versión de este plan: no hay nada con que comparar.</p>
        )}
        {datosImpacto !== null && (
          <>
            <div className="cifras">
              <Cifra n={datosImpacto.tareasAnadidas} t="+ añadidas" />
              <Cifra n={datosImpacto.tareasModificadas} t="~ modificadas" />
              <Cifra n={datosImpacto.tareasRetiradas} t="− retiradas" />
              <Cifra n={datosImpacto.otsImpactadas} t="OTs impactadas" />
            </div>
            {!hayDecisionQueTomar ? (
              <p className="pequena suave" style={{ marginTop: 8 }}>
                Este cambio no afecta a ninguna OT ya emitida.
              </p>
            ) : (
              <ResumenCambios impacto={datosImpacto} />
            )}
          </>
        )}
      </div>

      {hayDecisionQueTomar && datosImpacto !== null && (
        <div className="sep">
          <h3>Qué hacer con las OTs ya emitidas</h3>
          <p className="pequena suave">
            Este cambio afecta a {datosImpacto.otsImpactadas} OT{datosImpacto.otsImpactadas === 1 ? '' : 's'} ya emitida
            {datosImpacto.otsImpactadas === 1 ? '' : 's'}.
          </p>
          {datosImpacto.otsProtegidas > 0 && (
            <p className="pequena suave">
              {datosImpacto.otsProtegidas} contiene{datosImpacto.otsProtegidas === 1 ? '' : 'n'} actividad registrada y
              se conservará{datosImpacto.otsProtegidas === 1 ? '' : 'n'} tal cual, elija la que elija.
            </p>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
            {datosImpacto.estrategiasDisponibles.map((e) => (
              <label key={e} className="fila" style={{ gap: 8, alignItems: 'flex-start' }}>
                <input
                  type="radio"
                  name="estrategia-publicacion"
                  checked={estrategia === e}
                  onChange={() => setEstrategia(e)}
                  disabled={enviando}
                  style={{ marginTop: 3 }}
                />
                <span>
                  <div>
                    {TEXTO_ESTRATEGIA[e]!.titulo} <span className="suave pequena">({e})</span>
                  </div>
                  <div className="pequena suave">{TEXTO_ESTRATEGIA[e]!.explicacion}</div>
                </span>
              </label>
            ))}
          </div>

          {estrategia === 'D' && (
            <div className="sep">
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {datosImpacto.ots.map((o) => (
                  <li key={o.idOt} className="fila" style={{ gap: 10, padding: '6px 0', borderBottom: '1px solid var(--borde)' }}>
                    <span className="cod pequena">{o.codigoOt}</span>
                    {o.protegida ? (
                      <span className="pequena suave">
                        {/*  F10.4: el porqué, en claro — las protecciones no cambian, sólo se explican. */}
                        No puede sustituirse automáticamente: {textoProteccion(o.motivosProteccion ?? [])}.
                      </span>
                    ) : (
                      <select
                        aria-label={`Decisión para ${o.codigoOt}`}
                        value={decisionDe(o)}
                        disabled={enviando}
                        onChange={(ev) =>
                          setDecisiones((d) => ({ ...d, [o.idOt]: ev.target.value as DecisionOtPublicacion }))
                        }
                      >
                        <option value="MANTENER">{TEXTO_DECISION_OT.MANTENER}</option>
                        {o.clasificacion === 'COMPATIBLE' && (
                          <option value="APLICAR_COMPATIBLE">{TEXTO_DECISION_OT.APLICAR_COMPATIBLE}</option>
                        )}
                        {o.clasificacion === 'ESTRUCTURAL' && (
                          <option value="ANULAR_REEMITIR">{TEXTO_DECISION_OT.ANULAR_REEMITIR}</option>
                        )}
                      </select>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(estrategia === 'C' || estrategia === 'D') && (
            <div className="campo" style={{ marginTop: 10 }}>
              <label htmlFor="publicar-motivo">Motivo (opcional)</label>
              <input
                id="publicar-motivo"
                type="text"
                value={motivo}
                disabled={enviando}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Por qué se anulan y reemiten estas OTs"
              />
            </div>
          )}
        </div>
      )}

      {defaultsAsignacion.datos !== null && (
        <div className="sep">
          <h3>Asignaciones por defecto</h3>
          {(() => {
            const { cubiertas, sinAsignar } = resumenCobertura(defaultsAsignacion.datos.defaults, plan.lineas);
            return (
              <>
                <div className="cifras">
                  <Cifra n={defaultsAsignacion.datos.defaults.length} t="operarios" />
                  <Cifra n={cubiertas} t={`de ${String(plan.lineas)} tareas cubiertas`} />
                  <Cifra n={sinAsignar} t="sin asignar" />
                </div>
                <p className="pequena suave" style={{ marginTop: 8 }}>
                  Publicar no depende de esto: las tareas sin asignación por defecto se publican igual.
                </p>
              </>
            );
          })()}
        </div>
      )}
    </Modal>
  );
}

type InformeImpacto = Extract<ImpactoHipotetico, { sinVersionAnterior: false }>;

/**
 * FASE 6.5, FASE N: nunca el diff técnico de JSON. Un cambio se lee como
 * "+ Nueva tarea", "− Tarea eliminada", "~ Cambio de ubicación", etc. — la
 * causa de negocio que ya calculó impacto/servicio.ts, en prosa.
 */
function ResumenCambios({ impacto: i }: { impacto: InformeImpacto }): ReactNode {
  const lineas: string[] = [];
  if (i.tareasAnadidas > 0) lineas.push(`+ ${String(i.tareasAnadidas)} tarea${i.tareasAnadidas === 1 ? '' : 's'} nueva${i.tareasAnadidas === 1 ? '' : 's'}`);
  if (i.tareasRetiradas > 0) lineas.push(`− ${String(i.tareasRetiradas)} tarea${i.tareasRetiradas === 1 ? '' : 's'} eliminada${i.tareasRetiradas === 1 ? '' : 's'}`);
  if (i.tareasModificadas > 0) lineas.push(`~ ${String(i.tareasModificadas)} tarea${i.tareasModificadas === 1 ? '' : 's'} modificada${i.tareasModificadas === 1 ? '' : 's'}`);
  if (i.otsElegiblesC > 0) lineas.push(`~ ${String(i.otsElegiblesC)} OT${i.otsElegiblesC === 1 ? '' : 's'} con cambio estructural`);
  return (
    <ul className="pequena suave" style={{ margin: '8px 0 0', paddingLeft: 18 }}>
      {lineas.map((l) => (
        <li key={l}>{l}</li>
      ))}
    </ul>
  );
}

// --------------------------------------------------------------- descartar

function ModalDescartar({
  idPlan,
  cerrar,
  alDescartar,
}: {
  idPlan: string;
  cerrar: () => void;
  alDescartar: () => void;
}): ReactNode {
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function descartar(): Promise<void> {
    setEnviando(true);
    setError(null);
    try {
      await api.del(`/api/planes/${idPlan}`);
      alDescartar();
    } catch (e) {
      setError(e);
      setEnviando(false);
    }
  }

  return (
    <Modal
      titulo="Descartar borrador"
      cerrar={cerrar}
      pie={
        <>
          <span className="modal-pie-relleno" />
          {error !== null && <ErrorCaja error={error} />}
          <button className="peligro" disabled={enviando} onClick={() => void descartar()}>
            {enviando ? 'Descartando…' : 'Descartar borrador'}
          </button>
          <button disabled={enviando} onClick={cerrar}>
            Cancelar
          </button>
        </>
      }
    >
      <p>Este borrador nunca se ha publicado. Se eliminarán sus cambios.</p>
    </Modal>
  );
}
